import { StandardEvents } from '@shopify/events';

/**
 * "Email me when it's back" form (snippets/canopy-restock-notify.liquid).
 *
 * Shown only while the selected variant is sold out; follows variant changes through the
 * product-select event that the price/SKU components use. Posts to the canopy-restock
 * Worker, which emails once when the variant is back. Turnstile loads on first interaction
 * so shoppers who never touch the form never download it.
 */
class CanopyRestockNotify extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {string | null} */
  #widgetId = null;
  /** @type {string} */
  #token = '';
  /** @type {((token: string) => void) | null} */
  #tokenWaiter = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.form?.addEventListener('submit', this.#onSubmit, { signal });
    this.form?.addEventListener('focusin', () => this.#loadTurnstile(), { signal, once: true });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('[data-restock-form]'));
  }

  /** @param {any} event */
  #onProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const variant = detail?.resource;
        if (!variant || typeof variant !== 'object') {
          this.hidden = true;
          return;
        }
        this.hidden = Boolean(variant.available) || variant.inventory_management !== 'shopify';
        if (String(variant.id) === this.dataset.variantId) return;

        this.dataset.variantId = String(variant.id);
        this.dataset.variantTitle = variant.title === 'Default Title' ? '' : variant.title || '';
        const label = this.querySelector('[data-restock-label]');
        if (label) {
          label.textContent = this.dataset.variantTitle
            ? `${this.dataset.productTitle} (${this.dataset.variantTitle})`
            : this.dataset.productTitle || '';
        }
        this.#setStatus('');
        if (this.form) this.form.hidden = false;
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[canopy-restock] product select failed:', error);
      });
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const input = /** @type {HTMLInputElement} */ (form.elements.namedItem('email'));
    const honeypot = /** @type {HTMLInputElement} */ (form.elements.namedItem('website'));
    const button = form.querySelector('button[type="submit"]');

    const email = input.value.trim();
    if (!input.checkValidity() || !email) {
      this.#setStatus('Please enter a valid email address.', true);
      input.focus();
      return;
    }

    button?.setAttribute('disabled', '');
    this.#setStatus('Saving…');
    try {
      this.#loadTurnstile();
      const turnstile = await this.#getToken();
      const response = await fetch(this.dataset.endpoint || '', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          handle: this.dataset.handle,
          variant_id: Number(this.dataset.variantId),
          turnstile,
          website: honeypot?.value || '',
        }),
      });
      const result = await response.json().catch(() => ({}));

      if (response.ok && result.ok) {
        form.hidden = true;
        this.#setStatus(
          result.available
            ? 'Good news: it’s in stock right now. Refresh the page to add it to your cart.'
            : `You’re on the list. We’ll email ${email} when it’s back.`
        );
      } else {
        this.#setStatus(result.error || 'Something went wrong. Please try again.', true);
      }
    } catch (error) {
      console.warn('[canopy-restock] subscribe failed:', error);
      this.#setStatus('Something went wrong. Please try again, or email info@canopywatchco.com.', true);
    } finally {
      button?.removeAttribute('disabled');
      this.#resetTurnstile();
    }
  };

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-restock-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('canopy-restock__status--error', isError);
  }

  #loadTurnstile() {
    if (this.#widgetId !== null || !this.dataset.sitekey) return;
    this.#widgetId = '';
    const render = () => {
      const turnstile = /** @type {any} */ (window).turnstile;
      const container = this.querySelector('[data-restock-turnstile]');
      if (!turnstile || !container) return;
      this.#widgetId = turnstile.render(container, {
        sitekey: this.dataset.sitekey,
        appearance: 'interaction-only',
        size: 'flexible',
        callback: (/** @type {string} */ token) => {
          this.#token = token;
          this.#tokenWaiter?.(token);
        },
        'expired-callback': () => {
          this.#token = '';
        },
      });
    };

    if (/** @type {any} */ (window).turnstile) {
      render();
      return;
    }
    const callbackName = `canopyRestockTurnstile${Math.random().toString(36).slice(2)}`;
    /** @type {any} */ (window)[callbackName] = render;
    const script = document.createElement('script');
    script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callbackName}`;
    script.async = true;
    document.head.append(script);
  }

  /** @returns {Promise<string>} */
  #getToken() {
    if (this.#token) return Promise.resolve(this.#token);
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(''), 15000);
      this.#tokenWaiter = (token) => {
        clearTimeout(timer);
        this.#tokenWaiter = null;
        resolve(token);
      };
    });
  }

  #resetTurnstile() {
    this.#token = '';
    const turnstile = /** @type {any} */ (window).turnstile;
    if (turnstile && this.#widgetId) turnstile.reset(this.#widgetId);
  }
}

if (!customElements.get('canopy-restock-notify')) {
  customElements.define('canopy-restock-notify', CanopyRestockNotify);
}
